import React from 'react';
import { Mic2, Quote, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const PodcastImpact = () => {
  const testimonials = [
    {
      id: 1,
      name: 'Melanie',
      quote: 'Your conversation with Juan about ACEs and how childhood trauma leaves lasting tracks in the body and mind made me think deeply about the links between adversity, health, and healing. The way you talked about practical regulation strategies and rebuilding dignity really speaks to the importance of bridging scientific understanding with compassion and real-world support.'
    },
    {
      id: 2,
      name: 'Donna',
      quote: 'Hearing Juan share how routine, reflection, and faith provided a rhythm of healing in your latest episode really moved me. The emphasis on transforming adversity through connection and support reflects so much of what I witness daily in adoption work. Trauma may be the aftermath, but it\'s relationships and safe spaces that guide the way forward, a truth at the heart of both your podcast and my professional mission.'
    },
    {
      id: 3,
      name: 'Scott',
      quote: 'Your recent episode with Juan made a real impression on me, especially as you explored how the act of asking for help can change the entire trajectory of a life impacted by childhood adversity. The way you both highlighted that trauma is about the internal aftermath (not just the event itself) and discussed the four S\'s of secure bonding was powerfully done. I appreciate how you connected practical healing tools to everyday habits and hope.'
    },
    {
      id: 4,
      name: 'Evan',
      quote: 'Hearing Juan\'s story on your latest episode, especially his description of the painful split between the authentic self and the survival self, really struck me. The way you both explore how community becomes a "repair shop" for old wounds hit home.'
    },
    {
      id: 5,
      name: 'Elsa',
      quote: 'Your recent episode with Juan made a strong impact on me. The way you walked through the connection between a rough childhood, ACEs, and the brain\'s stress circuits was powerful. I especially appreciated how you moved from breaking down cortisol spikes and survival states into the practical reality of regulating with small, daily habits like breathwork and grounding. The compassion-first approach and the bridge you built between science and everyday experience really struck a chord.'
    },
    {
      id: 6,
      name: 'Jenny',
      quote: 'Your conversation with Juan about growth behind the wall was truly powerful. The way you both explored how trauma-informed practices can help people find their authentic selves, especially when discussing the split between who we are and who we\'re pressured to be, struck a real chord with me. Juan\'s story about a safe room and the importance of being seen made me think about how rarely history offers those kinds of restorative spaces, especially for those who survived some of humanity\'s darkest chapters.'
    }
  ];

  return (
    <section id="podcast" className="relative py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-6">
            <Mic2 className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-sm">PODCAST IMPACT</span>
          </div>
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            VOICES OF
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              TRANSFORMATION
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Listeners and participants sharing how trauma-informed conversations changed their perspective on healing, adversity, and hope
          </p>
        </div>

        {/* Intro */}
        <div className="mb-16 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-2xl p-8 md:p-12 text-center">
          <p className="text-2xl text-white font-semibold mb-4">
            "Stories that sparked connection, understanding, and healing"
          </p>
          <p className="text-gray-400 max-w-3xl mx-auto mb-6">
            Through honest conversations about ACEs, trauma, survival patterns, and the journey from pain to purpose, our podcast creates space for listeners to see themselves, their struggles, and their potential for transformation.
          </p>
          <Button
            onClick={() => window.open('https://www.iheart.com/podcast/269-healthy-living-by-willow-c-108166149/episode/secrets-and-healing-pt-6-healthy-living-through-adversity-327299594?viewTranscription=true', '_blank')}
            className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-8 py-4 transition-all duration-300 transform hover:scale-105"
          >
            <Mic2 className="w-5 h-5 mr-2" />
            LISTEN TO THE PODCAST
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-gray-500 text-sm mt-3">
            Healthy Living Through Adversity with J'Wells on Joe Grumbine's Podcast
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-xl p-8 transform hover:scale-105 transition-all duration-300 hover:border-[#D4AF37] relative"
            >
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 opacity-20">
                <Quote className="w-12 h-12 text-[#D4AF37]" />
              </div>

              {/* Content */}
              <div className="relative z-10">
                <p className="text-gray-300 leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center">
                    <span className="text-[#0a0a0a] font-bold text-sm">
                      {testimonial.name[0]}
                    </span>
                  </div>
                  <div>
                    <p className="text-[#D4AF37] font-bold">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-500 text-xs">Podcast Listener</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Impact Statement */}
        <div className="mt-16 bg-gradient-to-r from-[#1a1a1a] via-[#D4AF37]/5 to-[#1a1a1a] border border-[#D4AF37]/30 rounded-2xl p-8 md:p-12">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              HEALING THROUGH HONEST CONVERSATION
            </h3>
            <p className="text-xl text-gray-300 mb-4 leading-relaxed">
              Every episode creates space for understanding the science of trauma, the power of compassion, and the practical tools that support real healing. We explore topics from ACEs and survival patterns to the four S's of secure bonding and rebuilding authentic connection.
            </p>
            <p className="text-gray-400">
              When we share our stories and listen with compassion, transformation becomes possible—for ourselves and for our communities.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PodcastImpact;
